import { useEffect, useMemo, useState } from 'react';
import { Card, Table, Button, Spinner, Alert, Badge, Form, Modal } from 'react-bootstrap';
import type { components } from '@telemedicine/shared';
import { listAdminSpecialties, createSpecialty, updateSpecialty } from '../services/api';
import { BusyButton } from '../components/BusyButton';
import { useConfirm } from '../components/ConfirmProvider';
import { useToast } from '../components/ToastProvider';
import { errMessage } from '../errors';

type Specialty = components['schemas']['Specialty'];

/**
 * GLOBAL_ADMIN specialty taxonomy management. Two-level tree (category -> leaf). Specialties are
 * never hard-deleted: deactivating hides them from new-case pickers while existing cases keep
 * their snapshotted specialty name. Supports create, rename, reparent, activate/deactivate, and
 * launch-subset toggle, with cursor-based load-more.
 */
export function AdminSpecialties() {
  const [items, setItems] = useState<Specialty[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Specialty | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    listAdminSpecialties()
      .then((res) => {
        setItems(res.items);
        setCursor(res.nextCursor ?? null);
      })
      .catch((e) => setError(errMessage(e, 'Failed to load specialties')))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const loadMore = () => {
    if (!cursor) return;
    setLoadingMore(true);
    listAdminSpecialties({ cursor })
      .then((res) => {
        setItems((prev) => [...prev, ...res.items]);
        setCursor(res.nextCursor ?? null);
      })
      .catch((e) => setError(errMessage(e, 'Failed to load more')))
      .finally(() => setLoadingMore(false));
  };

  const categories = useMemo(() => items.filter((s) => s.isCategory), [items]);

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <div className="d-flex align-items-center mb-3">
          <Card.Title as="h2" className="h4 mb-0">
            Specialties
          </Card.Title>
          <div className="flex-grow-1" />
          <Button size="sm" onClick={() => setShowCreate(true)}>
            Add specialty
          </Button>
        </div>

        {loading && (
          <div className="text-muted d-flex align-items-center">
            <Spinner animation="border" size="sm" className="me-2" /> Loading specialties…
          </div>
        )}
        {error && <Alert variant="danger">{error}</Alert>}

        {!loading && !error && (
          <>
            <Table hover responsive className="align-middle mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Category</th>
                  <th>Launch subset</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => {
                  const parent = s.categoryId
                    ? items.find((c) => c.specialtyId === s.categoryId)
                    : undefined;
                  return (
                    <tr key={s.specialtyId} className={s.active ? '' : 'text-muted'}>
                      <td>{s.displayName}</td>
                      <td>
                        <Badge bg={s.isCategory ? 'dark' : 'secondary'}>
                          {s.isCategory ? 'Category' : 'Specialty'}
                        </Badge>
                      </td>
                      <td className="small">{parent?.displayName ?? (s.isCategory ? '—' : s.categoryId ?? '—')}</td>
                      <td>{s.launchSubset ? <Badge bg="info">Launch</Badge> : ''}</td>
                      <td>
                        {s.active ? (
                          <Badge bg="success">Active</Badge>
                        ) : (
                          <Badge bg="secondary">Inactive</Badge>
                        )}
                      </td>
                      <td className="text-end">
                        <Button variant="outline-secondary" size="sm" onClick={() => setEditing(s)}>
                          Edit
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            {cursor && (
              <div className="text-center mt-3">
                <Button variant="outline-secondary" size="sm" disabled={loadingMore} onClick={loadMore}>
                  {loadingMore ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            )}
          </>
        )}
      </Card.Body>

      {showCreate && (
        <CreateSpecialtyModal
          categories={categories}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            load();
          }}
        />
      )}
      {editing && (
        <EditSpecialtyModal
          specialty={editing}
          categories={categories}
          hasActiveLeaves={
            editing.isCategory &&
            items.some((s) => !s.isCategory && s.active && s.categoryId === editing.specialtyId)
          }
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </Card>
  );
}

function CreateSpecialtyModal({
  categories,
  onClose,
  onCreated,
}: {
  categories: Specialty[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [displayName, setDisplayName] = useState('');
  const [isCategory, setIsCategory] = useState(false);
  const [categoryId, setCategoryId] = useState('');
  const [launchSubset, setLaunchSubset] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isCategory && !categoryId) {
      setError('A specialty must belong to a category.');
      return;
    }
    setSaving(true);
    setError(null);
    createSpecialty({
      displayName,
      isCategory,
      categoryId: isCategory ? null : categoryId,
      launchSubset,
    })
      .then(() => {
        toast.success(`${isCategory ? 'Category' : 'Specialty'} "${displayName.trim()}" created.`);
        onCreated();
      })
      .catch((err) => setError(errMessage(err, 'Failed to create specialty')))
      .finally(() => setSaving(false));
  };

  return (
    <Modal show onHide={onClose} centered>
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Add specialty</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form.Group className="mb-3">
            <Form.Check
              type="radio"
              id="type-leaf"
              label="Specialty (belongs to a category)"
              checked={!isCategory}
              onChange={() => setIsCategory(false)}
            />
            <Form.Check
              type="radio"
              id="type-category"
              label="Category (top-level group)"
              checked={isCategory}
              onChange={() => setIsCategory(true)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              autoFocus
            />
          </Form.Group>
          {!isCategory && (
            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} required>
                <option value="">Select a category…</option>
                {categories.map((c) => (
                  <option key={c.specialtyId} value={c.specialtyId}>
                    {c.displayName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
          <Form.Check
            type="checkbox"
            id="launch-subset"
            label="Include in the curated launch subset"
            checked={launchSubset}
            onChange={(e) => setLaunchSubset(e.target.checked)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <BusyButton type="submit" loading={saving} busyLabel="Creating…">
            Create
          </BusyButton>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function EditSpecialtyModal({
  specialty,
  categories,
  hasActiveLeaves,
  onClose,
  onSaved,
}: {
  specialty: Specialty;
  categories: Specialty[];
  hasActiveLeaves: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [displayName, setDisplayName] = useState(specialty.displayName);
  const [categoryId, setCategoryId] = useState(specialty.categoryId ?? '');
  const [active, setActive] = useState(specialty.active);
  const [launchSubset, setLaunchSubset] = useState(specialty.launchSubset);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = useConfirm();
  const toast = useToast();

  const renamed = displayName.trim() !== specialty.displayName;
  const deactivating = specialty.active && !active;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Renaming is retroactive for the current-name view; existing cases keep their snapshot, but
    // the taxonomy and any resolve-by-id display change. Confirm so it is deliberate.
    if (
      renamed &&
      !(await confirm({
        title: 'Rename specialty?',
        body: `Rename "${specialty.displayName}" to "${displayName.trim()}"? Existing cases keep the name recorded at the time; new references will use the new name.`,
        confirmLabel: 'Rename',
        variant: 'warning',
      }))
    ) {
      return;
    }

    // Deactivating hides it from new cases (existing cases keep their snapshot). Confirm.
    if (
      deactivating &&
      !(await confirm({
        title: `Deactivate ${specialty.isCategory ? 'category' : 'specialty'}?`,
        body: `"${specialty.displayName}" will be hidden from new cases. Existing cases keep it. You can reactivate it later.`,
        confirmLabel: 'Deactivate',
        variant: 'danger',
      }))
    ) {
      return;
    }

    setSaving(true);
    setError(null);
    updateSpecialty(specialty.specialtyId, {
      version: specialty.version,
      displayName: displayName.trim(),
      // Only send categoryId for leaves that changed parent.
      categoryId:
        !specialty.isCategory && categoryId && categoryId !== specialty.categoryId
          ? categoryId
          : undefined,
      active,
      launchSubset,
    })
      .then(() => {
        toast.success(`"${displayName.trim()}" updated.`);
        onSaved();
      })
      .catch((err) => setError(errMessage(err, 'Failed to update specialty')))
      .finally(() => setSaving(false));
  };

  return (
    <Modal show onHide={onClose} centered>
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Edit {specialty.isCategory ? 'category' : 'specialty'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
          </Form.Group>
          {!specialty.isCategory && (
            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                {categories.map((c) => (
                  <option key={c.specialtyId} value={c.specialtyId}>
                    {c.displayName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
          <Form.Check
            type="switch"
            id="edit-active"
            label={active ? 'Active' : 'Inactive (hidden from new cases)'}
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            // A category cannot be deactivated while it still has active specialties (the server
            // enforces this too); disable the toggle with a hint so it isn't attempted.
            disabled={specialty.isCategory && specialty.active && hasActiveLeaves}
            className="mb-2"
          />
          {specialty.isCategory && specialty.active && hasActiveLeaves && (
            <Form.Text className="text-muted d-block mb-2">
              Deactivate or move this category's specialties before deactivating the category.
            </Form.Text>
          )}
          <Form.Check
            type="checkbox"
            id="edit-launch"
            label="Include in the curated launch subset"
            checked={launchSubset}
            onChange={(e) => setLaunchSubset(e.target.checked)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <BusyButton type="submit" loading={saving} busyLabel="Saving…">
            Save
          </BusyButton>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
